import apiClient from '../axios.js';

export const orderService = {
  /**
   * 查詢訂單列表
   * @param {Object} params - 查詢參數
   * @param {number} params.page - 頁碼
   * @param {number} params.pageSize - 每頁筆數
   * @param {string} params.memberId - 會員 ID（選填）
   * @param {string} params.storeId - 門市 ID（選填）
   * @param {string} params.status - 訂單狀態（選填）
   * @param {string} params.paymentStatus - 付款狀態（選填）
   * @param {string} params.startDate - 開始日期（選填，YYYY-MM-DD）
   * @param {string} params.endDate - 結束日期（選填，YYYY-MM-DD）
   * @param {string} params.search - 搜尋關鍵字（選填）
   * @returns {Promise<Object>} 訂單列表
   */
  getOrders: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);
    if (params.memberId) queryParams.append('memberId', params.memberId);
    if (params.storeId) queryParams.append('storeId', params.storeId);
    if (params.status) queryParams.append('status', params.status);
    if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    const url = `/orders${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response;
  },

  /**
   * 查詢未綁定訂單列表
   * @param {Object} params - 查詢參數
   * @param {number} params.page - 頁碼
   * @param {number} params.pageSize - 每頁筆數
   * @param {string} params.status - 訂單狀態（選填）
   * @param {string} params.paymentStatus - 付款狀態（選填）
   * @param {string} params.startDate - 開始日期（選填，YYYY-MM-DD）
   * @param {string} params.endDate - 結束日期（選填，YYYY-MM-DD）
   * @param {string} params.search - 搜尋關鍵字（選填）
   * @returns {Promise<Object>} 未綁定訂單列表
   */
  getUnboundOrders: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);
    if (params.status) queryParams.append('status', params.status);
    if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    const url = `/orders/unbound${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response;
  },

  /**
   * 查詢訂單詳情
   * @param {string} orderId - 訂單 ID
   * @returns {Promise<Object>} 訂單詳情
   */
  getOrder: async (orderId) => {
    const response = await apiClient.get(`/orders/${orderId}`);
    return response;
  },

  /**
   * 建立訂單（手動建立）
   * @param {Object} data - 訂單資料
   * @param {string} data.memberId - 會員 ID（選填）
   * @param {string} data.storeId - 門市 ID（選填）
   * @param {string} data.orderType - 訂單類型
   * @param {number} data.subtotal - 小計
   * @param {number} data.discountAmount - 折扣金額
   * @param {number} data.shippingFee - 運費
   * @param {number} data.taxAmount - 稅額
   * @param {number} data.totalAmount - 總金額
   * @param {number} data.paidAmount - 已付金額
   * @param {string} data.paymentMethod - 付款方式
   * @param {string} data.paymentStatus - 付款狀態
   * @param {string} data.status - 訂單狀態
   * @param {Array} data.items - 訂單明細
   * @returns {Promise<Object>} 建立結果
   */
  createOrder: async (data) => {
    const response = await apiClient.post('/orders', data);
    return response;
  },

  /**
   * 更新訂單狀態
   * @param {string} orderId - 訂單 ID
   * @param {Object} data - 更新資料
   * @param {string} data.status - 新狀態
   * @param {string} data.notes - 備註（選填）
   * @returns {Promise<Object>} 更新結果
   */
  printLogisticsDocuments: async (orderIds) => {
    const response = await apiClient.post('/orders/logistics-documents', { orderIds });
    return response;
  },

  updateOrderStatus: async (orderId, data) => {
    const response = await apiClient.patch(`/orders/${orderId}/status`, data);
    return response;
  },

  reissueEinvoice: async (orderId) => {
    const response = await apiClient.post(`/orders/${orderId}/einvoice/issue`);
    return response;
  },

  /**
   * 已付款缺貨補償處理
   * @param {string} orderId
   * @param {{action: 'refunded'|'backorder', remarks?: string}} data
   */
  handlePaidOutOfStockCompensation: async (orderId, data) => {
    const response = await apiClient.patch(`/orders/${orderId}/paid-out-of-stock-compensation`, data);
    return response;
  },

  /**
   * 查詢訂單變更歷程
   * @param {string} orderId - 訂單 ID
   * @returns {Promise<Object>} 變更歷程列表
   */
  getOrderChangeLogs: async (orderId) => {
    const response = await apiClient.get(`/orders/${orderId}/change-logs`);
    return response;
  },

  /**
   * 試算訂單可獲得點數
   * @param {Object} data
   * @param {string|null} data.memberId
   * @param {number} data.totalAmount
   * @param {number} data.paidAmount
   * @param {string|null} data.paymentStatus
   * @param {string|null} data.orderDate
   */
  estimatePoints: async (data) => {
    const response = await apiClient.post('/orders/estimate-points', data);
    return response;
  },
};
